// The builder gate on the SPA side (docs/follow-ups-plan.md §3, §5). Every
// AppShell route sits inside it, inside ProtectedRoute: a login whose
// GET /api/me says `capabilities.builder === false` is sent to Follow-ups. The
// API refuses such a login on every builder route anyway; this only keeps the
// person off pages that would fail.
//
// The one exemption (Miles, 2026-09-27): the Accommodations console, whose API
// is gated by the RRA roster rather than the builder. A route may name it via
// `withoutBuilder`, and a login without the builder whose
// `capabilities.accommodations` is true gets that element (the console in the
// minimal shell) instead of the redirect.
//
// The builder is open unless the capability says otherwise, so with the
// BUILDER_REQUIRE_ADMIN switch off nobody is redirected.

import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth, type Capabilities } from '@/contexts/AuthContext';

export const FOLLOW_UPS_PATH = '/follow-ups';
export const ACCOMMODATIONS_PATH = '/accommodations';
const BUILDER_HOME_PATH = '/overview';

/** A link in the minimal shell's nav. */
export interface MinimalNavLink {
  label: string;
  path: string;
}

/**
 * The pages a login may use outside the builder, as the minimal shell's links:
 * Follow-ups always, Accommodations when the capability says so — never a
 * builder page. Undefined when Follow-ups is the only one (a nav holding just
 * the page you are on says nothing, and Follow-ups keeps its bare header).
 */
export function minimalNavFor(capabilities: Capabilities | null): MinimalNavLink[] | undefined {
  if (capabilities?.accommodations !== true) return undefined;
  return [
    { label: 'Follow-ups', path: FOLLOW_UPS_PATH },
    { label: 'Accommodations', path: ACCOMMODATIONS_PATH },
  ];
}

function CapabilitiesLoading() {
  return (
    <div
      role="status"
      style={{
        display: 'flex',
        minHeight: '100vh',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--fg-muted)',
        background: 'var(--bg)',
      }}
    >
      Loading...
    </div>
  );
}

export function RequireBuilder({
  children,
  withoutBuilder,
}: {
  children: ReactNode;
  /**
   * What a login WITHOUT the builder gets here instead of the redirect, when
   * the named capability is true. Only the Accommodations console uses it.
   */
  withoutBuilder?: { capability: 'accommodations'; element: ReactNode };
}) {
  const { capabilities } = useAuth();
  if (capabilities === null) return <CapabilitiesLoading />;
  if (capabilities.builder !== false) return <>{children}</>;
  if (withoutBuilder && capabilities[withoutBuilder.capability] === true) {
    return <>{withoutBuilder.element}</>;
  }
  return <Navigate to={FOLLOW_UPS_PATH} replace />;
}

/** Where `/` and unknown paths land: Follow-ups for a login without the builder, else Overview. */
export function homePathFor(capabilities: Capabilities | null): string {
  return capabilities?.builder === false ? FOLLOW_UPS_PATH : BUILDER_HOME_PATH;
}

/** The `/` and `*` redirect. While capabilities load it goes to Overview, whose gate then decides. */
export function HomeRedirect() {
  const { capabilities } = useAuth();
  return <Navigate to={homePathFor(capabilities)} replace />;
}
